import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Chip,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  MoneyOff as MoneyOffIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import axios from "axios";

function Row({ user, onDebtCleared }) {
  const [open, setOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, lessonId: null, type: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleClearDebt = async (lessonId = null) => {
    setLoading(true);
    setError("");
    try {
      const url = lessonId
        ? `${process.env.REACT_APP_BACKEND_ADDRESS}/user/${user.user_id}/clear-debt/${lessonId}`
        : `${process.env.REACT_APP_BACKEND_ADDRESS}/user/${user.user_id}/clear-debt`;

      const response = await axios.post(url, {}, {
        headers: { "Access-Token": process.env.REACT_APP_API_TOKEN },
      });

      if (response.status === 200) {
        setConfirmDialog({ open: false, lessonId: null, type: null });
        onDebtCleared();
      }
    } catch (err) {
      console.error("Failed to clear debt", err);
      setError("Nie udało się wyczyścić długu");
    } finally {
      setLoading(false);
    }
  };

  const openConfirmDialog = (lessonId = null, type = "all") => {
    setConfirmDialog({ open: true, lessonId, type });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, lessonId: null, type: null });
  };

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {user.full_name}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {user.email}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Chip
            label={`${user.debt_count} zajęć`}
            color="warning"
            size="small"
          />
        </TableCell>
        <TableCell align="right">
          <Typography variant="body1" sx={{ fontWeight: 600, color: "error.main" }}>
            {user.debt_amount} PLN
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => openConfirmDialog(null, "all")}
            disabled={loading}
            startIcon={<MoneyOffIcon />}
          >
            Wyczyść wszystko
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom component="div">
                Szczegóły długu
              </Typography>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <Table size="small" aria-label="debt details">
                <TableHead>
                  <TableRow>
                    <TableCell>Nazwa zajęć</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell align="right">Kwota</TableCell>
                    <TableCell align="right">Akcja</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {user.lessons.map((lesson) => (
                    <TableRow key={lesson.lesson_id}>
                      <TableCell>{lesson.lesson_name}</TableCell>
                      <TableCell>
                        {new Date(lesson.lesson_date).toLocaleDateString("pl-PL", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </TableCell>
                      <TableCell align="right">{lesson.price} PLN</TableCell>
                      <TableCell align="right">
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => openConfirmDialog(lesson.lesson_id, "single")}
                          disabled={loading}
                        >
                          Wyczyść
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        aria-labelledby="confirm-dialog-title"
      >
        <DialogTitle id="confirm-dialog-title">
          Potwierdzenie wyczyszczenia długu
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.type === "all"
              ? `Czy na pewno chcesz wyczyścić cały dług (${user.debt_amount} PLN) dla użytkownika ${user.full_name}?`
              : `Czy na pewno chcesz wyczyścić ten dług dla użytkownika ${user.full_name}?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} disabled={loading}>
            Anuluj
          </Button>
          <Button
            onClick={() => handleClearDebt(confirmDialog.lessonId)}
            color="primary"
            variant="contained"
            disabled={loading}
            autoFocus
          >
            {loading ? <CircularProgress size={20} /> : "Wyczyść"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const DebtManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchUsersWithDebt = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/user/debt-management/`,
        {
          headers: { "Access-Token": process.env.REACT_APP_API_TOKEN },
        }
      );

      if (response.status === 200 && response.data.users) {
        setUsers(response.data.users);
        setFilteredUsers(response.data.users);
      }
    } catch (err) {
      console.error("Failed to fetch users with debt", err);
      setError("Nie udało się załadować użytkowników z długiem");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsersWithDebt();
  }, []);

  useEffect(() => {
    if (searchTerm === "") {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(
        (user) =>
          user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const handleDebtCleared = () => {
    fetchUsersWithDebt();
  };

  const totalDebt = users.reduce((sum, user) => sum + user.debt_amount, 0);
  const totalUsers = users.length;

  return (
    <Box sx={{ width: "100%", p: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
          Zarządzanie długami użytkowników
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchUsersWithDebt}
          disabled={loading}
        >
          Odśwież
        </Button>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Paper sx={{ p: 2, flex: "1 1 200px", minWidth: 200 }}>
          <Typography variant="body2" color="textSecondary">
            Użytkownicy z długiem
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "warning.main" }}>
            {totalUsers}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: "1 1 200px", minWidth: 200 }}>
          <Typography variant="body2" color="textSecondary">
            Całkowity dług
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "error.main" }}>
            {totalDebt} PLN
          </Typography>
        </Paper>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Szukaj użytkownika po nazwisku lub emailu..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && filteredUsers.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {searchTerm
            ? "Nie znaleziono użytkowników pasujących do wyszukiwania"
            : "Nie ma użytkowników z długiem. Świetnie!"}
        </Alert>
      )}

      {!loading && !error && filteredUsers.length > 0 && (
        <TableContainer component={Paper}>
          <Table aria-label="debt management table">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Użytkownik</TableCell>
                <TableCell align="right">Liczba zajęć</TableCell>
                <TableCell align="right">Kwota długu</TableCell>
                <TableCell align="right">Akcje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <Row
                  key={user.user_id}
                  user={user}
                  onDebtCleared={handleDebtCleared}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default DebtManagement;
